import { AlertTriangle, Lock, UserX, Maximize2, Minimize2 } from "lucide-react";
import { toast } from "sonner";
import { useMemo } from "react";
import type { Node } from "@xyflow/react";
import type { GraphNodeData } from "./graph/types";

interface NodeContextMenuProps {
  isOpen: boolean;
  position?: { x: number; y: number };
  nodeId: string | null;
  nodeLabel: string;
  nodeType: string | null;
  focusMode: boolean;
  hasExpandedChildren: boolean;
  onClose: () => void;
  onShowFullGraph: (nodeId: string) => void;
  onCollapseStep: (nodeId: string) => void;
  onToggleFocusMode: () => void;
  flowNodes?: Node<GraphNodeData>[];
  getViewport?: () => { x: number; y: number; zoom: number };
  containerRef?: React.RefObject<HTMLDivElement>;
  viewportTrigger?: number;
}

export function NodeContextMenu({ 
  isOpen, 
  position: fallbackPosition,
  nodeId, 
  nodeLabel, 
  nodeType,
  focusMode,
  hasExpandedChildren,
  onClose,
  onShowFullGraph,
  onCollapseStep,
  onToggleFocusMode,
  flowNodes,
  getViewport,
  containerRef,
  viewportTrigger,
}: NodeContextMenuProps) {
  // Compute screen position from flow node position (bottom-right corner)
  const position = useMemo(() => {
    if (!isOpen || !nodeId) return fallbackPosition || { x: 0, y: 0 };
    
    // If flow data is available, compute from node position
    if (flowNodes && getViewport && containerRef) {
      const node = flowNodes.find(n => n.id === nodeId);
      if (node) {
        const viewport = getViewport();
        const containerRect = containerRef.current?.getBoundingClientRect();
        const containerLeft = containerRect?.left || 0;
        const containerTop = containerRect?.top || 0;

        const isCard = node.type === 'cardNode';
        const nodeWidth = isCard ? 640 : 90;
        const nodeHeight = isCard ? 220 : 90;

        const flowX = node.position.x + nodeWidth;
        const flowY = node.position.y + nodeHeight;

        const screenX = flowX * viewport.zoom + viewport.x + containerLeft;
        const screenY = flowY * viewport.zoom + viewport.y + containerTop;

        return { x: screenX + 8, y: screenY + 8 };
      }
    }
    
    return fallbackPosition || { x: 0, y: 0 };
  }, [isOpen, nodeId, flowNodes, getViewport, containerRef, fallbackPosition, viewportTrigger]);

  if (!isOpen || !nodeId) return null;

  const canShowFullGraph = nodeType === 'user' || nodeType === 'service_account' || nodeType === 'resource';

  const handleAction = (action: string) => {
    switch (action) {
      case 'remove-privilege':
        toast.success(`Removing privileges from ${nodeLabel}`);
        break;
      case 'revoke-access':
        toast.success(`Access revoked for ${nodeLabel}`);
        break;
      case 'mark-critical':
        toast.warning(`${nodeLabel} marked as critical`);
        break;
      case 'show-full-graph':
        if (nodeId) {
          if (focusMode) {
            onToggleFocusMode();
          }
          onShowFullGraph(nodeId);
        }
        break;
      case 'collapse-step':
        if (nodeId && hasExpandedChildren) {
          onCollapseStep(nodeId);
          toast.info(`Collapsed children of ${nodeLabel}`);
        }
        break;
    }
    onClose();
  };

  return (
    <>
      {/* Backdrop to close menu */}
      <div 
        className="fixed inset-0 z-50" 
        onClick={onClose}
        onContextMenu={(e) => {
          e.preventDefault();
          onClose();
        }}
      />
      
      {/* Context Menu - positioned at node's bottom-right corner */}
      <div
        className="fixed z-50 min-w-[12rem] overflow-hidden rounded-md bg-popover p-1 text-popover-foreground shadow-md animate-in fade-in-80 zoom-in-95"
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
        }}
      >
        <div
          onClick={() => handleAction('remove-privilege')}
          className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          <UserX className="mr-2 h-4 w-4" />
          <span>Remove Privilege</span>
        </div>
        
        <div
          onClick={() => handleAction('revoke-access')}
          className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          <Lock className="mr-2 h-4 w-4" />
          <span>Revoke Access</span>
        </div>
        
        <div
          onClick={() => handleAction('mark-critical')}
          className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground text-destructive"
        >
          <AlertTriangle className="mr-2 h-4 w-4" />
          <span>Mark as Critical</span>
        </div>
        
        {hasExpandedChildren && (
          <>
            <div className="my-1 h-px bg-border" />
            <div
              onClick={() => handleAction('collapse-step')}
              className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              <Minimize2 className="mr-2 h-4 w-4" />
              <span>Collapse Step</span>
            </div>
          </>
        )}
        
        {canShowFullGraph && (
          <>
            <div className="my-1 h-px bg-border" />
            <div
              onClick={() => handleAction('show-full-graph')}
              className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              <Maximize2 className="mr-2 h-4 w-4" />
              <span>Show Full Graph</span>
            </div>
          </>
        )}
      </div>
    </>
  );
}
